import { useCallback, useEffect, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { APP_BASE, APP_HEADER } from "./app";
import { useAuth } from "./auth";
import { useApi } from "./index";
import type { AuthMethods, MfaEnrolment, SessionResponse, SignInChallenge, SignInStage } from "./types";

/**
 * The strict staff sign-in (security §2.6 with SPEC C13/C42): password → (forced change of a temporary password) → session. A host that
 * sets `Auth:RequireMfa` adds (authenticator enrolment) → authenticator code before the session; ECONIC does not. Until the session opens
 * no token exists: each stage holds a single-use server challenge, so nothing half-authenticated reaches storage.
 */
export type SignInStep = "password" | SignInStage | "done";

/** The surface the session is for; the refresh cookie is bound to it. */
export type SignInSurface = "assistant" | "backoffice" | "admin" | "portal";

export interface SignInError {
  /**
   * The server's problem code (`invalid_credentials`, `rate_limited`, `challenge_expired`, `mfa_invalid`, `mfa_code_reused`,
   * `password_policy`, `mfa_not_performed`), or `network` / `unavailable` (a server without the strict sign-in) / `password_mismatch`.
   */
  code: string;
  status?: number;
  /** Code attempts left on this challenge (`mfa_invalid`). */
  attemptsLeft?: number | null;
  /** Password policy failures (`password_too_short`, `password_common`, …). */
  errors?: string[];
}

export interface PasswordSignIn {
  step: SignInStep;
  busy: boolean;
  error: SignInError | null;
  /** The login as typed, for the masked account line of the code step. */
  login: string;
  /** The account came in with a temporary password (first sign-in or an administrator reset). */
  firstLogin: boolean;
  /** When the current challenge lapses (10 min). */
  expiresAt: string | null;
  enrolment: MfaEnrolment | null;
  session: SessionResponse | null;
  submitPassword: (login: string, password: string) => Promise<void>;
  changePassword: (newPassword: string, confirm?: string) => Promise<void>;
  startEnrolment: () => Promise<void>;
  confirmEnrolment: (code: string) => Promise<void>;
  submitCode: (code: string) => Promise<void>;
  /** Back to the first step ("Not you?", an expired challenge). */
  restart: (error?: SignInError | null) => void;
  /** Shows a check that failed in the browser (`code_incomplete`, `password_mismatch`, `password_too_short`) without a request. */
  fail: (code: string) => void;
  clearError: () => void;
}

interface Answer<T> {
  ok: boolean;
  status: number;
  body: T | null;
  problem: { code?: string; error?: string; attemptsLeft?: number; errors?: string[] | Record<string, string[]> } | null;
}

async function post<T>(base: string, path: string, body: unknown): Promise<Answer<T>> {
  const res = await fetch(`${base}${path}`, {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json", Accept: "application/json", [APP_HEADER]: APP_BASE },
    body: JSON.stringify(body),
  });
  let json: unknown = null;
  try {
    json = await res.json();
  } catch {
    json = null;
  }
  return res.ok ? { ok: true, status: res.status, body: json as T, problem: null } : { ok: false, status: res.status, body: null, problem: json as Answer<T>["problem"] };
}

function toError(a: Answer<unknown>): SignInError {
  if (a.status === 429) return { code: "rate_limited", status: 429 };
  if (a.status === 404 || a.status === 405) return { code: "unavailable", status: a.status };
  const p = a.problem;
  const code = p?.code ?? p?.error ?? (a.status === 401 ? "invalid_credentials" : "generic");
  const errors = Array.isArray(p?.errors) ? p.errors : p?.errors && typeof p.errors === "object" ? Object.values(p.errors).flat() : undefined;
  return { code, status: a.status, attemptsLeft: typeof p?.attemptsLeft === "number" ? p.attemptsLeft : null, errors };
}

const onlyDigits = (code: string) => code.replace(/\D/g, "").slice(0, 6);

export function usePasswordSignIn(options: { surface: SignInSurface; onSignedIn?: (s: SessionResponse) => void }): PasswordSignIn {
  const api = useApi();
  const { setSession } = useAuth();
  const base = api.baseUrl;
  const [step, setStep] = useState<SignInStep>("password");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<SignInError | null>(null);
  const [login, setLogin] = useState("");
  const [firstLogin, setFirstLogin] = useState(false);
  const [expiresAt, setExpiresAt] = useState<string | null>(null);
  const [enrolment, setEnrolment] = useState<MfaEnrolment | null>(null);
  const [session, setDone] = useState<SessionResponse | null>(null);
  const challenge = useRef<string | null>(null);
  const alive = useRef(true);
  const onSignedIn = useRef(options.onSignedIn);
  onSignedIn.current = options.onSignedIn;

  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
    };
  }, []);

  const restart = useCallback((e: SignInError | null = null) => {
    challenge.current = null;
    setStep("password");
    setEnrolment(null);
    setExpiresAt(null);
    setFirstLogin(false);
    setError(e);
  }, []);

  /** Runs one stage; network failures and an expired challenge are handled the same way everywhere. */
  const run = useCallback(
    async <T,>(path: string, body: unknown, onOk: (b: T) => void | Promise<void>) => {
      setBusy(true);
      setError(null);
      try {
        const a = await post<T>(base, path, body);
        if (!alive.current) return;
        if (a.ok && a.body) await onOk(a.body);
        else {
          const e = toError(a);
          if (e.code === "challenge_expired") restart(e);
          else setError(e);
        }
      } catch {
        if (alive.current) setError({ code: "network" });
      } finally {
        if (alive.current) setBusy(false);
      }
    },
    [base, restart],
  );

  const finish = useCallback(
    (s: SessionResponse) => {
      challenge.current = null;
      setSession(s, { arrival: true });
      setDone(s);
      setStep("done");
      onSignedIn.current?.(s);
    },
    [setSession],
  );

  const startEnrolment = useCallback(async () => {
    if (!challenge.current) return restart({ code: "challenge_expired" });
    await run<MfaEnrolment>("/api/auth/mfa/enrol/start", { challenge: challenge.current }, (e) => setEnrolment(e));
  }, [run, restart]);

  const advance = useCallback(
    async (c: SignInChallenge | SessionResponse) => {
      // Without a second factor the password (or the first-time change) opens the session at once.
      if (!("next" in c) || !c.next) return finish(c as SessionResponse);
      challenge.current = c.challenge;
      setExpiresAt(c.expiresAt);
      setStep(c.next);
      if (c.next === "password_change") setFirstLogin(true);
      if (c.next === "mfa_enrol") {
        setEnrolment(null);
        // Idempotent per challenge: the QR is there when the step opens.
        const a = await post<MfaEnrolment>(base, "/api/auth/mfa/enrol/start", { challenge: c.challenge }).catch(() => null);
        if (!alive.current) return;
        if (a?.ok && a.body) setEnrolment(a.body);
        else if (a) setError(toError(a));
        else setError({ code: "network" });
      }
    },
    [base, finish],
  );

  const submitPassword = useCallback(
    async (rawLogin: string, password: string) => {
      const l = rawLogin.normalize("NFKC").trim();
      setLogin(l);
      if (!l || !password) return setError({ code: "invalid_credentials" });
      await run<SignInChallenge | SessionResponse>("/api/auth/password", { login: l, password, surface: options.surface }, advance);
    },
    [run, advance, options.surface],
  );

  const changePassword = useCallback(
    async (newPassword: string, confirm?: string) => {
      if (confirm !== undefined && confirm !== newPassword) return setError({ code: "password_mismatch" });
      if (!challenge.current) return restart({ code: "challenge_expired" });
      await run<SignInChallenge | SessionResponse>("/api/auth/password/change", { challenge: challenge.current, newPassword }, advance);
    },
    [run, advance, restart],
  );

  const confirmEnrolment = useCallback(
    async (code: string) => {
      if (!challenge.current) return restart({ code: "challenge_expired" });
      await run<SessionResponse>("/api/auth/mfa/enrol/confirm", { challenge: challenge.current, code: onlyDigits(code) }, finish);
    },
    [run, finish, restart],
  );

  const submitCode = useCallback(
    async (code: string) => {
      if (!challenge.current) return restart({ code: "challenge_expired" });
      await run<SessionResponse>("/api/auth/mfa", { challenge: challenge.current, code: onlyDigits(code) }, finish);
    },
    [run, finish, restart],
  );

  return {
    step,
    busy,
    error,
    login,
    firstLogin,
    expiresAt,
    enrolment,
    session,
    submitPassword,
    changePassword,
    startEnrolment,
    confirmEnrolment,
    submitCode,
    restart,
    fail: useCallback((code: string) => setError({ code }), []),
    clearError: useCallback(() => setError(null), []),
  };
}

/**
 * `GET /api/auth/methods`; null while loading and on a server without the strict sign-in. With `surface`, `password` says whether that
 * app still signs in with a password (ECONIC may sign in with the Ministry account only, 28 Oct 2026).
 */
export function useAuthMethods(surface?: SignInSurface): AuthMethods | null {
  const api = useApi();
  const q = useQuery({
    queryKey: ["auth", "methods", surface ?? null],
    queryFn: async () => {
      const res = await fetch(`${api.baseUrl}/api/auth/methods${surface ? `?surface=${surface}` : ""}`, { headers: { Accept: "application/json" } });
      if (!res.ok) return null;
      return (await res.json()) as AuthMethods;
    },
    staleTime: 10 * 60_000,
    retry: false,
  });
  return q.data ?? null;
}

/**
 * Whether this server asks for an authenticator code after the password (`Auth:RequireMfa`). ECONIC signs staff in with the password
 * alone, so this is false there; screens show authenticator steps, columns and actions only when it is true. False while loading.
 */
export function useMfaRequired(): boolean {
  return useAuthMethods()?.mfaRequired === true;
}

/** "n•••@moet.gov.ae" / "d•••.aksenov": enough to recognise the account on the code step, not to read it off the screen. */
export function maskLogin(login: string): string {
  const l = login.trim();
  if (!l) return "";
  const at = l.indexOf("@");
  if (at > 0) return `${l[0]}•••${l.slice(at)}`;
  const dot = l.indexOf(".");
  if (dot > 0) return `${l[0]}•••${l.slice(dot)}`;
  return `${l[0]}•••`;
}

/** Advisory checks shown while typing a new password; the server's policy decides (security §2.3, SPEC C42: 14–128 characters). */
export const PASSWORD_MIN_LENGTH = 14;
export const PASSWORD_MAX_LENGTH = 128;

export interface PasswordRuleState {
  length: boolean;
  notTooLong: boolean;
  noIdentity: boolean;
  matches: boolean;
}

export function passwordRules(password: string, confirm: string, login: string): PasswordRuleState {
  const length = [...password.normalize("NFKC")].length;
  const local = login.toLowerCase().split("@")[0] ?? "";
  const folded = password.toLowerCase();
  return {
    length: length >= PASSWORD_MIN_LENGTH,
    notTooLong: length <= PASSWORD_MAX_LENGTH,
    noIdentity: !local || local.length < 3 || !folded.includes(local),
    matches: confirm.length > 0 && confirm === password,
  };
}
